/** Boot guard — refuse to render the Memso app on a hostname that
 *  should be serving a different app.
 *
 *  Why this exists: a misconfigured Cloudflare Pages custom-domain
 *  attachment once routed mail.3dbypixel.com (which should serve the
 *  Mail/WOV app) to the memso-app Pages project, and the Memso SPA
 *  silently rendered the onboarding wizard on a mail.* URL. The user
 *  saw the wrong app and we did not notice until much later.
 *
 *  The guard reads the hostname registry (synced from waki-shell)
 *  and asserts: "this bundle is the Memso bundle; the host I'm
 *  rendering on is registered as a Memso hostname." On mismatch we
 *  hard-redirect to the correct counterpart URL so the user lands on
 *  the right app instead of a broken UI.
 *
 *  Unknown hostnames (unconfigured custom domains, local dev hosts
 *  not in the registry) pass through silently — there's no canonical
 *  app to redirect to. */
import { findHostnameEntry, getCounterpartUrl } from "./hostnames";

export type GuardOutcome =
  | { status: "ok" }
  | { status: "unknown-host"; host: string }
  | { status: "redirected"; from: string; to: string }
  | { status: "mismatch-no-counterpart"; host: string; expected: string };

/** Run the guard. Returns synchronously; if it triggers a redirect,
 *  the page is already navigating away. Callers should check the
 *  result if they want to suppress further boot work (registering
 *  SW, kicking off auth polls) but it's safe to keep going either
 *  way — once `window.location.replace` is queued the page is
 *  effectively gone. */
const APP_KIND = "memso" as const;

export function runBootGuard(): GuardOutcome {
  if (typeof window === "undefined") return { status: "ok" };
  const host = window.location.host.toLowerCase();
  const entry = findHostnameEntry(host);
  if (!entry) {
    // Unknown host (preview URL, custom domain not yet in registry,
    // dev port). No safe redirect; let it render.
    return { status: "unknown-host", host };
  }
  if (entry.app === APP_KIND) return { status: "ok" };

  // Wrong app for this hostname. Try to redirect to the correct app
  // for the same brand using the registry's counterpart link.
  const cp = getCounterpartUrl(host);
  if (cp && cp.counterpartApp === APP_KIND) {
    // The registry says "this host should serve <other app>, and the
    // counterpart serves <this app>". The correct action is to send
    // the user to the host that actually matches this bundle.
    const target = cp.counterpartUrl + window.location.pathname.replace(/^\//, "") + window.location.search + window.location.hash;
    if (typeof console !== "undefined") {
      // eslint-disable-next-line no-console
      console.error(
        `[boot-guard] Memso bundle served on ${host} (registry says this is the ${entry.app} host). Redirecting to ${target}.`,
      );
    }
    window.location.replace(target);
    return { status: "redirected", from: host, to: target };
  }

  // Registry says this host is for the other app but the counterpart
  // chain doesn't loop back to us cleanly. Don't render the wrong app
  // — replace the splash with a clear error.
  showHostMismatchError(host, entry.app);
  return { status: "mismatch-no-counterpart", host, expected: entry.app };
}

function showHostMismatchError(host: string, expected: string): void {
  if (typeof document === "undefined") return;
  const splash = document.getElementById("memso-splash");
  if (splash) splash.remove();
  const root = document.getElementById("root") || document.body;
  root.innerHTML = `
    <div style="
      min-height: 100vh; display: flex; flex-direction: column;
      align-items: center; justify-content: center; padding: 2rem;
      background: #0b1220; color: #e8eefb; font-family: ui-sans-serif, system-ui, sans-serif;
      text-align: center;
    ">
      <div style="max-width: 28rem;">
        <div style="font-size: 1.25rem; font-weight: 600; margin-bottom: 0.5rem;">
          This hostname is not configured for Memso.
        </div>
        <div style="opacity: 0.75; font-size: 0.95rem; line-height: 1.5;">
          The hostname <code>${host}</code> is registered as a <strong>${expected}</strong> surface, but you've reached the Memso bundle. The deploy is misconfigured.
        </div>
        <div style="opacity: 0.55; font-size: 0.8rem; margin-top: 1.5rem;">
          Boot guard refused to render the wrong app.
        </div>
      </div>
    </div>
  `;
}
